import { useMemo } from "react"
import { FormProvider } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { SelectOption } from "shared/types/Select"
import { DimConfiguration } from "shared/types/__generated__/graphql"
import { Button } from "shared/ui/buttons/Button"
import { Toggle } from "shared/ui/input/boolean/toggle/Toggle"
import { Select } from "shared/ui/input/select/select/Select"
import { TextInput } from "shared/ui/input/text/TextInput"

import { useDimConfigurationAdminPageViewModel } from "./model/useDimConfigurationAdminPageViewModel"
import "./styles/_dim-configuration-admin-page.scss"
import { DimModelCategory } from "./types/DimModelCategory"
import { DimConfigurationDeleteButton } from "./ui/DimConfigurationDeleteButton"

export interface DimConfigurationAdminPageViewProps {
  configurationsForm: ReturnType<
    typeof useDimConfigurationAdminPageViewModel
  >["configurationsForm"]
  newConfigurationForm: ReturnType<
    typeof useDimConfigurationAdminPageViewModel
  >["newConfigurationForm"]
  handleDelete: (index: number) => Promise<void>
  handleAddConfiguration: () => Promise<void>
  configurations: DimConfiguration[]
  isLoading: boolean
  error?: Error
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
}

export function DimConfigurationAdminPageView({
  configurationsForm,
  newConfigurationForm,
  handleDelete,
  handleAddConfiguration,
  configurations,
  isLoading,
  error,
  isCreating,
  isUpdating,
  isDeleting
}: DimConfigurationAdminPageViewProps) {
  const { t } = useTranslation()

  const getCategoryLabel = useMemo(() => {
    return (category: string): string => {
      switch (category) {
        case DimModelCategory.JET_OUTLETS:
          return t("DimConfiguration.ModelCategory.JetOutlets")
        case DimModelCategory.VENTILATION_GRILLES:
          return t("DimConfiguration.ModelCategory.VentilationGrilles")
        case DimModelCategory.FILTER_DIFFUSERS:
          return t("DimConfiguration.ModelCategory.FilterDiffusers")
        case DimModelCategory.SHUT_OFF_DEVICES:
          return t("DimConfiguration.ModelCategory.ShutOffDevices")
        case DimModelCategory.SOUND_ATTENUATOR:
          return t("DimConfiguration.ModelCategory.SoundAttenuator")
        case DimModelCategory.SOUND_ABSORBER:
          return t("DimConfiguration.ModelCategory.SoundAbsorber")
        case DimModelCategory.VOLUMETRIC_FLOW:
          return t("DimConfiguration.ModelCategory.VolumetricFlow")
        case DimModelCategory.FIRE_PROTECTION:
          return t("DimConfiguration.ModelCategory.FireProtection")
        case DimModelCategory.AIR_WATER_SYSTEMS:
          return t("DimConfiguration.ModelCategory.AirWaterSystems")
        default:
          return category
      }
    }
  }, [t])

  const productTypeOptions: SelectOption<string>[] = useMemo(() => {
    return Object.values(DimModelCategory).map(category => ({
      value: category,
      label: getCategoryLabel(category)
    }))
  }, [getCategoryLabel])

  if (isLoading) {
    return (
      <div className="dim-configuration-admin-page">
        <div className="dim-configuration-admin-page__loading">
          {t("General.Loading")}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="dim-configuration-admin-page">
        <div className="dim-configuration-admin-page__error">
          {t("General.Error")}: {error.message}
        </div>
      </div>
    )
  }

  return (
    <div className="dim-configuration-admin-page">
      <div className="dim-configuration-admin-page__header">
        <h1 className="dim-configuration-admin-page__title">
          {t("DimConfiguration.Admin.Title")}
        </h1>
        <p className="dim-configuration-admin-page__description">
          {t("DimConfiguration.Admin.Description")}
        </p>
      </div>

      <FormProvider {...configurationsForm}>
        <form className="dim-configuration-admin-page__form">
          <div className="dim-configuration-admin-page__list">
            {configurations?.map((config, index) => (
              <div
                key={config.id}
                className="dim-configuration-admin-page__item"
              >
                <div className="dim-configuration-admin-page__item-content">
                  <div className="dim-configuration-admin-page__item-info">
                    <div className="dim-configuration-admin-page__item-row">
                      <strong>
                        {t("DimConfiguration.Fields.ProductName")}:
                      </strong>{" "}
                      {config.productName}
                    </div>
                    <div className="dim-configuration-admin-page__item-row">
                      <strong>
                        {t("DimConfiguration.Fields.ProductType")}:
                      </strong>{" "}
                      {getCategoryLabel(config.productType)}
                    </div>
                    {config.dimensioningKey && (
                      <div className="dim-configuration-admin-page__item-row">
                        <strong>
                          {t("DimConfiguration.Fields.DimensioningKey")}:
                        </strong>{" "}
                        {config.dimensioningKey}
                      </div>
                    )}
                  </div>

                  <div className="dim-configuration-admin-page__item-actions">
                    <Toggle.Root
                      name={`configurations.${index}.isActive`}
                      label={t("DimConfiguration.Fields.IsActive")}
                      disabled={isUpdating}
                    >
                      <Toggle.Input />
                    </Toggle.Root>
                    <DimConfigurationDeleteButton
                      index={index}
                      onDelete={handleDelete}
                      disabled={isDeleting}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </form>
      </FormProvider>

      <div className="dim-configuration-admin-page__add-room">
        <h2 className="dim-configuration-admin-page__add-title">
          {t("DimConfiguration.Admin.AddNew")}
        </h2>
        <FormProvider {...newConfigurationForm}>
          <form
            className="dim-configuration-admin-page__add-form"
            onSubmit={handleAddConfiguration}
          >
            <TextInput.Root
              name="productName"
              label={t("DimConfiguration.Fields.ProductName")}
              className="dim-configuration-admin-page__add-field"
              required
            >
              <TextInput.Label />
              <TextInput.Focus>
                <TextInput.Input
                  type="text"
                  placeholder={t("DimConfiguration.Placeholders.ProductName")}
                />
              </TextInput.Focus>
              <TextInput.ErrorMessage />
            </TextInput.Root>

            <Select.Root
              name="productType"
              label={t("DimConfiguration.Fields.ProductType")}
              className="dim-configuration-admin-page__add-field"
              required
            >
              <Select.Label />
              <Select.Input
                options={productTypeOptions}
                placeholder={t("DimConfiguration.Placeholders.ProductType")}
              />
              <Select.ErrorMessage />
            </Select.Root>

            <TextInput.Root
              name="dimensioningKey"
              label={t("DimConfiguration.Fields.DimensioningKey")}
              className="dim-configuration-admin-page__add-field"
            >
              <TextInput.Label />
              <TextInput.Focus>
                <TextInput.Input
                  type="text"
                  placeholder={t(
                    "DimConfiguration.Placeholders.DimensioningKey"
                  )}
                />
              </TextInput.Focus>
              <TextInput.ErrorMessage />
            </TextInput.Root>

            <Button
              variant="primary"
              type="submit"
              disabled={isCreating}
              className="dim-configuration-admin-page__add-button"
            >
              {isCreating
                ? t("General.Create")
                : t("DimConfiguration.Admin.AddButton")}
            </Button>
          </form>
        </FormProvider>
      </div>
    </div>
  )
}
